import { formatLocalDateTime } from '../utils/localDateTime';
import React, { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { Organization, UserProfile } from '@types';

type TicketSummary = {
    id: string;
    category: string;
    subject: string;
    description: string;
    priority: string;
    status: 'open' | 'in-progress' | 'resolved' | 'closed';
    consultantReply?: string;
    createdAt: string;
    updatedAt: string;
    statusUpdatedAt?: string | null;
    resolvedAt?: string | null;
};

const statusColors: Record<TicketSummary['status'], string> = {
    open: 'bg-red-100 text-red-700',
    'in-progress': 'bg-amber-100 text-amber-700',
    resolved: 'bg-emerald-100 text-emerald-700',
    closed: 'bg-slate-100 text-slate-600',
};

function dateLabel(value: string | null | undefined): string {
    return formatLocalDateTime(value, 'Date unavailable');
}

const MyHelpdeskTickets: React.FC<{ currentUser: UserProfile; organization: Organization; refreshKey: number }> = ({ currentUser, organization, refreshKey }) => {
    const [tickets, setTickets] = useState<TicketSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let active = true;
        let busy = false;
        const load = async () => {
            if (busy)
                return;
            busy = true;
            try {
                const callable = httpsCallable<undefined, { tickets: TicketSummary[] }>(getFunctions(undefined, 'us-central1'), 'listOwnHelpdeskTickets');
                const result = await callable();
                if (active) {
                    setTickets(result.data.tickets);
                    setError(false);
                    setLoading(false);
                }
            } catch (cause) {
                if (active) {
                    console.error('Support tickets failed loading.', cause);
                    setError(true);
                    setLoading(false);
                }
            } finally {
                busy = false;
            }
        };
        const refreshVisible = () => {
            if (document.visibilityState === 'visible')
                void load();
        };
        void load();
        const timer = window.setInterval(refreshVisible, 60000);
        document.addEventListener('visibilitychange', refreshVisible);
        return () => {
            active = false;
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', refreshVisible);
        };
    }, [currentUser.uid, organization.id, refreshKey]);

    return <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4"><h2 className="text-lg font-extrabold text-slate-900">My Support Tickets</h2><p className="mt-1 text-sm text-slate-500">Track your requests and consultant replies.</p></div>
        {loading && <p className="text-sm text-slate-500">Loading tickets...</p>}
        {error && <p className="text-sm text-red-600">Tickets could not load. Retrying shortly.</p>}
        {!loading && !error && tickets.length === 0 && <p className="text-sm text-slate-500">You have no support tickets yet.</p>}
        {!loading && !error && tickets.length > 0 && <div className="space-y-3">
            {tickets.map(ticket => <article key={ticket.id} className="rounded-xl border border-slate-200 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div><p className="text-xs font-semibold text-slate-500">{ticket.category} · #{ticket.id.slice(-6)}</p><h3 className="mt-1 font-bold text-slate-900">{ticket.subject}</h3></div>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold capitalize ${statusColors[ticket.status] || 'bg-slate-100 text-slate-600'}`}>{ticket.status.replace('-', ' ')}</span>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600">{ticket.description}</p>
                {ticket.consultantReply && <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50 p-3"><p className="text-xs font-bold text-blue-700">Consultant reply</p><p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{ticket.consultantReply}</p></div>}
                <p className="mt-3 text-xs text-slate-400">Submitted {dateLabel(ticket.createdAt)} · Updated {dateLabel(ticket.statusUpdatedAt || ticket.updatedAt)}</p>
            </article>)}
        </div>}
    </section>;
};

export default MyHelpdeskTickets;
